<template>
  <div class="mt-root">
    <div v-if="loading" class="mt-center">
      <v-progress-circular indeterminate size="26" width="2" color="primary" />
    </div>

    <template v-else-if="messages.length">
      <!-- Kundenfeedback 14.09.26: "In Outlook sehe ich bei einer E-Mail den
           kompletten Gespraechsverlauf, ich kann ihn aufklappen." Genau das
           macht diese Leiste: der Verlauf ist zusammengeklappt, die neueste
           Nachricht offen — und laesst sich in einem Zug ausklappen. -->
      <div v-if="messages.length > 1" class="mt-bar">
        <span class="mt-bar__count">
          {{ $gt('{n} Nachrichten im Verlauf').replace('{n}', messages.length) }}
        </span>
        <button type="button" class="mt-bar__toggle" @click="toggleAll">
          <ChevronsUpDown :size="13" />
          <span>{{ allExpanded ? $gt('Alle einklappen') : $gt('Alle aufklappen') }}</span>
        </button>
      </div>

      <article
        v-for="m in messages"
        :key="m.id"
        class="mt-msg"
        :class="{ 'mt-msg--collapsed': !isExpanded(m) }"
      >
        <!-- Der Kopf ist der Schalter: Klick klappt die Nachricht auf/zu.
             Bei einer einzelnen Nachricht bleibt er ein reiner Kopf. -->
        <component
          :is="collapsible ? 'button' : 'div'"
          class="mt-msg__head"
          :type="collapsible ? 'button' : null"
          :aria-expanded="collapsible ? String(isExpanded(m)) : null"
          @click="collapsible && toggle(m)"
        >
          <ChevronDown v-if="collapsible" :size="14" class="mt-msg__caret" />
          <span class="mt-msg__avatar">
            <img v-if="m.image" :src="m.image" alt="" />
            <span v-else class="mt-msg__initials">{{ initialsFor(m) }}</span>
          </span>
          <span class="mt-msg__who">{{ senderFor(m) }}</span>
          <span v-if="isExternal(m)" class="mt-msg__tag">{{ $gt('Extern') }}</span>
          <span v-if="!isExpanded(m)" class="mt-msg__peek">{{ previewFor(m) }}</span>
          <Paperclip v-if="m.attachments && m.attachments.length" :size="12" class="mt-msg__clip" />
          <span class="mt-msg__when">{{ formatDateTime(m.createdAt) }}</span>
        </component>

        <!-- Nachricht als PDF an eine neue Nachricht anhaengen (Inhaber-
             Feedback 15.08.26, I.12). Bewusst nur an der aufgeklappten
             Nachricht: in der zugeklappten Zeile waere kein Platz und die
             Aktion gehoert zum Inhalt, nicht zur Uebersicht. -->
        <button
          v-if="forwardable && isExpanded(m)"
          type="button"
          class="mt-msg__fwd"
          :title="$gt('Als Anhang weiterleiten (PDF)')"
          :aria-label="$gt('Als Anhang weiterleiten (PDF)')"
          :disabled="exportingMessageId === m.id"
          @click="$emit('forward', m)"
        >
          <v-progress-circular v-if="exportingMessageId === m.id" indeterminate size="12" width="2" />
          <FileOutput v-else :size="13" />
          <span>{{ $gt('Als Anhang weiterleiten') }}</span>
        </button>

        <!-- Absender & Richtung festlegen – dieselbe Aktion wie im Chat-Verlauf
             (Angleichung 03.10.2026). Nur wo der Aufrufer sie umsetzt. -->
        <button
          v-if="classifiable && isExpanded(m) && canClassify(m)"
          type="button"
          class="mt-msg__fwd"
          data-testid="thread-classify"
          :title="$gt('Festlegen, ob diese E-Mail eingegangen oder gesendet ist und von wem sie stammt – z. B. bei einer aus Outlook weitergeleiteten oder sich selbst geschickten E-Mail')"
          :aria-label="$gt('Absender & Richtung festlegen')"
          @click="$emit('classify', m)"
        >
          <ArrowLeftRight :size="13" />
          <span>{{ $gt('Absender & Richtung') }}</span>
        </button>

        <template v-if="isExpanded(m)">
          <div class="mt-msg__body" v-html="m.content"></div>

          <!-- Zustellstatus: beantwortet direkt an der Nachricht die Frage
               "Ist das angekommen?" -->
          <div
            v-if="deliveryBadge(m)"
            class="mt-delivery"
            :class="`mt-delivery--${deliveryBadge(m).tone}`"
          >
            <CheckCircle2 v-if="deliveryBadge(m).tone === 'ok'" :size="13" />
            <Clock v-else-if="deliveryBadge(m).tone === 'pending'" :size="13" />
            <AlertTriangle v-else :size="13" />
            <span class="mt-delivery__label">{{ deliveryBadge(m).label }}</span>
            <span v-if="deliveryBadge(m).detail" class="mt-delivery__detail">
              {{ deliveryBadge(m).detail }}
            </span>
            <!-- "Zustellung läuft": Abgleich mit dem Versanddienst (Feedback 02.10.26, I.). -->
            <button
              v-if="canRefreshDelivery(deliveryBadge(m))"
              type="button"
              class="mt-delivery__refresh"
              :disabled="refreshingId === m.id"
              :title="$gt('Zustellstatus beim Versanddienst abfragen')"
              @click="refreshStatus(m)"
            >
              <RefreshCw :size="12" :class="{ 'mt-spin': refreshingId === m.id }" />
              <span>{{ $gt('Status prüfen') }}</span>
            </button>
          </div>

          <!-- Uebertragungs-/Lesebestaetigungen (Inhaber-Feedback I.3) -->
          <MessageReceipts v-if="receipts" :receipts="m.receipts" />

          <div v-if="m.attachments && m.attachments.length" class="mt-msg__atts">
            <button
              v-for="a in m.attachments"
              :key="a.id"
              type="button"
              class="mt-att"
              @click="$emit('download', { message: m, attachment: a })"
            >
              <Paperclip :size="13" />
              <span>{{ a.filename }}</span>
              <span class="mt-att__size">{{ formatFileSize(a.fileSize) }}</span>
            </button>
          </div>
        </template>
      </article>
    </template>

    <p v-else class="mt-center mt-muted">{{ emptyLabel }}</p>
  </div>
</template>

<script>
/**
 * Gesprächsverlauf einer Konversation — die gemeinsame Darstellung für die
 * globale Kommunikation UND die Fallebene (Kundenfeedback 14.09.26:
 * "Alle Funktionen müssen identisch sein. Auch das User Interface!").
 *
 * Bewusst reine Darstellung: Laden, Antworten und Zustellabfragen bleiben
 * beim Aufrufer, damit derselbe Verlauf im Lesebereich des Postfachs und am
 * Posteingangs-Eintrag ohne Sonderfälle wiederverwendbar ist.
 */
import {
  Paperclip, ChevronDown, ChevronsUpDown,
  CheckCircle2, Clock, AlertTriangle, FileOutput, RefreshCw, ArrowLeftRight,
} from "lucide-vue-next";
import { canChangeDirection } from "@/components/messages/chat/chatModel";
import { canRefreshDelivery, deliveryBadge } from "@/components/messages/chat/deliveryBadge";
import { useDeliveryRefresh } from "@/components/messages/chat/useDeliveryRefresh";
import MessageReceipts from "@/components/messages/MessageReceipts.vue";

export default {
  name: "MessageThread",

  components: {
    MessageReceipts,
    Paperclip, ChevronDown, ChevronsUpDown,
    CheckCircle2, Clock, AlertTriangle, FileOutput, RefreshCw, ArrowLeftRight,
  },

  emits: ["download", "forward", "delivery-refreshed", "classify"],

  setup() {
    // "Status prüfen" (Feedback 02.10.26, I.): gemeinsame Logik mit dem Chat-Verlauf.
    const { refreshingId, refresh } = useDeliveryRefresh();
    return { refreshingId, refreshDeliveryStatus: refresh };
  },

  props: {
    messages: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Leerzustand, damit jeder Aufrufer seinen eigenen Text setzen kann. */
    emptyLabel: { type: String, default: "" },
    /** Bestätigungen anzeigen (im schwebenden Fenster ebenfalls erwünscht). */
    receipts: { type: Boolean, default: true },
    /** "Als Anhang weiterleiten" anbieten — nur wo der Aufrufer es umsetzt. */
    forwardable: { type: Boolean, default: false },
    /** "Absender & Richtung" anbieten — nur wo der Aufrufer es umsetzt. */
    classifiable: { type: Boolean, default: false },
    /** Nachricht, deren PDF gerade erzeugt wird (Spinner am Knopf). */
    exportingMessageId: { type: [Number, String], default: null },
  },

  data() {
    return {
      /** IDs der Nachrichten, deren Zustand vom Standard abweicht. */
      overrides: {},
      forceAll: null,
      /** Nach "Status prüfen" aktualisierte Zustellzusammenfassungen je Nachricht. */
      deliveryOverrides: {},
    };
  },

  computed: {
    /** Ab zwei Nachrichten lohnt das Auf-/Zuklappen. */
    collapsible() {
      return this.messages.length > 1;
    },
    lastId() {
      return this.messages.length ? this.messages[this.messages.length - 1].id : null;
    },
    allExpanded() {
      return this.messages.every((m) => this.isExpanded(m));
    },
  },

  watch: {
    /**
     * Beim Wechsel der Konversation zurück auf den Standard: nur die neueste
     * Nachricht offen. Ohne das Zurücksetzen würde ein aufgeklappter Verlauf
     * in der nächsten Konversation nachwirken.
     */
    messages() {
      this.overrides = {};
      this.forceAll = null;
    },
  },

  methods: {
    /** Nur E-Mails (keine internen Notizen, keine Zustellhinweise). */
    canClassify(m) {
      return canChangeDirection(m);
    },

    isExpanded(m) {
      if (!this.collapsible) return true;
      if (Object.prototype.hasOwnProperty.call(this.overrides, m.id)) return this.overrides[m.id];
      if (this.forceAll !== null) return this.forceAll;
      return m.id === this.lastId;
    },

    toggle(m) {
      this.overrides = { ...this.overrides, [m.id]: !this.isExpanded(m) };
    },

    toggleAll() {
      this.forceAll = !this.allExpanded;
      this.overrides = {};
    },

    /**
     * Absender einer Nachricht. Eingehende E-Mails haben keinen KARS-Nutzer
     * (idUser ist NULL) — dort stehen Name und Adresse des Externen in
     * senderName/senderEmail.
     */
    senderFor(m) {
      if (!m) return "";
      return m.userName || m.senderName || m.senderEmail || this.$gt("Unbekannt");
    },

    initialsFor(m) {
      if (m?.initials) return m.initials;
      const parts = String(this.senderFor(m) || "").trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
      return "?";
    },

    isExternal(m) {
      return Boolean(m && !m.userName && (m.senderEmail || m.senderName));
    },

    previewFor(m) {
      const text = String(m?.content || "")
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return text || this.$gt("(Kein Inhalt)");
    },

    /** Zustell-Kennzeichnung (gemeinsame Logik mit dem Chat-Verlauf). */
    deliveryBadge(m) {
      const summary = (m && this.deliveryOverrides[m.id]) || m?.delivery;
      return deliveryBadge(summary, (src) => this.$gt(src));
    },

    canRefreshDelivery,

    /** Zustellstatus beim Versanddienst abfragen und lokal einsetzen. */
    async refreshStatus(m) {
      const summary = await this.refreshDeliveryStatus(m.id);
      if (!summary) return;
      this.deliveryOverrides = { ...this.deliveryOverrides, [m.id]: summary };
      this.$emit("delivery-refreshed", m.id, summary);
    },

    formatDateTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE");
    },

    formatFileSize(bytes) {
      const n = Number(bytes || 0);
      if (n < 1024) return `${n} B`;
      if (n < 1048576) return `${Math.round(n / 1024)} KB`;
      return `${(n / 1048576).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.mt-root { display: flex; flex-direction: column; gap: 12px; }

.mt-center { display: flex; justify-content: center; padding: 30px; }
.mt-muted { color: var(--grey-color); font-size: 0.83rem; }

/* ── Verlaufsleiste ── */
.mt-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.mt-bar__count { font-size: 0.74rem; color: var(--grey-color); }
.mt-bar__toggle {
  display: inline-flex; align-items: center; gap: 5px;
  border: none; background: transparent; cursor: pointer;
  font-size: 0.74rem; font-weight: 600; color: var(--primary-color);
  padding: 2px 4px; border-radius: 6px;
}
.mt-bar__toggle:hover { background: var(--primary-light-color); }

/* ── Nachricht ── */
.mt-msg {
  background: #fff;
  border: 1px solid var(--border-grey-color);
  border-radius: 10px;
  padding: 12px 14px;
}
.mt-msg--collapsed { padding: 4px 14px; background: #fbfcfe; }

.mt-msg__head {
  display: flex; align-items: center; gap: 9px;
  width: 100%; margin-bottom: 8px;
  padding: 0; border: none; background: transparent;
  font: inherit; color: inherit; text-align: left;
}
button.mt-msg__head { cursor: pointer; }
.mt-msg--collapsed .mt-msg__head { margin-bottom: 0; padding: 6px 0; }
button.mt-msg__head:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
  border-radius: 6px;
}

.mt-msg__caret {
  flex-shrink: 0; color: var(--grey-color);
  transition: transform 0.18s ease;
}
.mt-msg--collapsed .mt-msg__caret { transform: rotate(-90deg); }

.mt-msg__avatar {
  width: 28px; height: 28px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center;
  background: #6b7280; color: #fff;
  flex-shrink: 0;
}
.mt-msg__avatar img { width: 100%; height: 100%; object-fit: cover; }
.mt-msg__initials { font-size: 0.72rem; font-weight: 600; }

.mt-msg__who {
  flex-shrink: 0;
  font-size: 0.82rem; font-weight: 600; color: var(--dark-color);
}
/* Aufgeklappt fuellt der Name die Zeile, zugeklappt teilt er sie mit der
   Vorschau — dadurch bleibt die Zeilenhoehe in beiden Zustaenden gleich. */
.mt-msg:not(.mt-msg--collapsed) .mt-msg__who { flex: 1; min-width: 0; }

.mt-msg__peek {
  flex: 1; min-width: 0;
  font-size: 0.78rem; color: var(--grey-color);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mt-msg__clip { flex-shrink: 0; color: var(--grey-color); }
.mt-msg__when { flex-shrink: 0; font-size: 0.72rem; color: var(--grey-color); }
.mt-msg__tag {
  flex-shrink: 0;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary-color);
  background: var(--primary-light-color);
  border-radius: 4px;
  padding: 1px 6px;
}

/* ── Nachrichtentext ──
   Eingehende E-Mails werden serverseitig zu HTML normalisiert
   (internal/mail/mailbody): Absätze, Zitate und der einklappbare
   Nachrichtenverlauf brauchen hier ihre Darstellung. */
.mt-msg__body { font-size: 0.86rem; line-height: 1.55; color: var(--dark-color); overflow-wrap: anywhere; }
.mt-msg__body :deep(img) { max-width: 100%; height: auto; }
.mt-msg__body :deep(p) { margin: 0 0 0.55em; }
.mt-msg__body :deep(p:last-child) { margin-bottom: 0; }
.mt-msg__body :deep(a) { color: var(--primary-color); }
.mt-msg__body :deep(pre) { white-space: pre-wrap; font-family: inherit; margin: 0 0 0.55em; }
.mt-msg__body :deep(table) { border-collapse: collapse; max-width: 100%; }
.mt-msg__body :deep(td), .mt-msg__body :deep(th) { border: 1px solid var(--border-grey-color-light); padding: 4px 8px; }
.mt-msg__body :deep(blockquote) {
  margin: 0.5em 0;
  padding: 0.1em 0 0.1em 0.75em;
  border-left: 3px solid var(--primary-light-color);
  color: var(--grey-color);
}
.mt-msg__body :deep(.kars-quoted-history) { margin-top: 0.6em; }
.mt-msg__body :deep(.kars-quoted-history > summary) {
  cursor: pointer;
  display: inline-block;
  list-style: none;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--primary-color);
  padding: 2px 0;
}
.mt-msg__body :deep(.kars-quoted-history > summary::-webkit-details-marker) { display: none; }
.mt-msg__body :deep(.kars-quoted-history[open] > summary) { margin-bottom: 0.4em; }

/* ── Zustellverfolgung ──
   Die Kennzeichnung sitzt bewusst direkt unter dem Nachrichtentext: Dort
   sucht der Anwender die Antwort auf "Ist das angekommen?". Farbe trägt die
   Aussage nie allein — jede Variante hat zusätzlich Symbol und Text. */
.mt-delivery {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 0.6rem;
  padding: 6px 10px;
  border-radius: 7px;
  font-size: 0.74rem;
  line-height: 1.45;
  border: 1px solid transparent;
}
.mt-delivery__label { font-weight: 600; white-space: nowrap; }
.mt-delivery__detail { opacity: 0.85; overflow-wrap: anywhere; }
.mt-delivery__refresh {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}
.mt-delivery__refresh:disabled { cursor: progress; opacity: 0.7; }
.mt-spin { animation: mt-spin 0.9s linear infinite; }
@keyframes mt-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .mt-spin { animation: none; } }
.mt-delivery--ok {
  color: #15803d;
  background: rgba(22, 163, 74, 0.08);
  border-color: rgba(22, 163, 74, 0.22);
}
.mt-delivery--pending {
  color: #a16207;
  background: rgba(202, 138, 4, 0.08);
  border-color: rgba(202, 138, 4, 0.22);
}
.mt-delivery--error {
  color: #b91c1c;
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.25);
}

/* ── Als Anhang weiterleiten ── */
.mt-msg__fwd {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
  padding: 3px 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: var(--grey-color);
  font-size: 0.72rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s ease, background 0.12s ease, color 0.12s ease;
}
.mt-msg:hover .mt-msg__fwd,
.mt-msg__fwd:focus-visible { opacity: 1; }
.mt-msg__fwd:hover { background: var(--primary-light-color); color: var(--primary-color); border-color: var(--primary-color); }
.mt-msg__fwd:disabled { opacity: 1; cursor: default; }

/* ── Anhänge ── */
.mt-msg__atts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.mt-att {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 9px;
  border: 1px solid var(--border-grey-color);
  border-radius: 7px;
  background: #f8fafc;
  font-size: 0.75rem;
  color: var(--dark-color);
  cursor: pointer;
}
.mt-att:hover { background: #eef4ff; border-color: #cfe0ff; }
.mt-att__size { color: var(--grey-color); }
</style>
